import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Package, Plus, Tag, X } from '../ui/icons';
import Card from '../ui/Card';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Colors from '../../constants/Colors';
import { productApi, Product } from '../../services/api';

interface OfferRow {
  offerId: string;
  codes: string;
}

function offersToRows(offers: Record<string, string[]>): OfferRow[] {
  return Object.entries(offers || {}).map(([offerId, codes]) => ({
    offerId,
    codes: (codes || []).join(', '),
  }));
}

function rowsToOffers(rows: OfferRow[]): Record<string, string[]> {
  const offers: Record<string, string[]> = {};
  rows.forEach((row) => {
    const offerId = row.offerId.trim();
    if (!offerId) return;

    const codes = Array.from(
      new Set(
        row.codes
          .split(',')
          .map((code) => code.trim())
          .filter(Boolean)
      )
    );
    if (codes.length === 0) return;

    offers[offerId] = codes;
  });
  return offers;
}

const columnLabelStyle: React.CSSProperties = {
  fontSize: '12px',
  fontWeight: 700,
  letterSpacing: '0.03em',
  textTransform: 'uppercase',
  color: '#5F719A',
};

const CONTAINS_SPACE = /\s/;

/** Offer IDs and codes are typed/matched exactly against the mobile app, so no
 * internal spaces are allowed - leading/trailing spaces are trimmed above before
 * this check runs, so only spaces between characters are left to catch here. */
function findSpacedValue(rows: OfferRow[]): string | null {
  for (const row of rows) {
    const offerId = row.offerId.trim();
    if (offerId && CONTAINS_SPACE.test(offerId)) {
      return `Offer ID "${offerId}" cannot contain spaces.`;
    }

    const codes = row.codes.split(',').map((code) => code.trim()).filter(Boolean);
    for (const code of codes) {
      if (CONTAINS_SPACE.test(code)) {
        return `Code "${code}" cannot contain spaces.`;
      }
    }
  }
  return null;
}

export default function ProductsPanel() {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const [managingProduct, setManagingProduct] = useState<Product | null>(null);
  const [offerRows, setOfferRows] = useState<OfferRow[]>([]);

  const loadProducts = async () => {
    setLoading(true);
    setError(null);

    const response = await productApi.listProducts();
    if (response.success && response.data) {
      setProducts(response.data.products || []);
    } else {
      setError(response.error || 'Failed to load products');
    }

    setLoading(false);
  };

  useEffect(() => {
    loadProducts();
  }, []);

  const filteredProducts = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return products;
    return products.filter(
      (p) =>
        p.product_id.toLowerCase().includes(query) || p.product_name.toLowerCase().includes(query)
    );
  }, [products, search]);

  const openManageOffers = (product: Product) => {
    setManagingProduct(product);
    setOfferRows(offersToRows(product.offers));
  };

  const closeManageOffers = () => setManagingProduct(null);

  const addOfferRow = () => setOfferRows((rows) => [...rows, { offerId: '', codes: '' }]);

  const removeOfferRow = (index: number) =>
    setOfferRows((rows) => rows.filter((_, i) => i !== index));

  const updateOfferRow = (index: number, field: keyof OfferRow, value: string) =>
    setOfferRows((rows) => rows.map((row, i) => (i === index ? { ...row, [field]: value } : row)));

  const handleSaveOffers = async () => {
    if (!managingProduct) return;

    const blankOfferIdWithCodes = offerRows.some((row) => !row.offerId.trim() && row.codes.trim());
    if (blankOfferIdWithCodes) {
      toast.error('Every offer needs an Offer ID.');
      return;
    }

    const spacedValueError = findSpacedValue(offerRows);
    if (spacedValueError) {
      toast.error(spacedValueError);
      return;
    }

    const offers = rowsToOffers(offerRows);

    setSaving(true);
    const response = await productApi.updateProductOffers(
      managingProduct.product_id,
      managingProduct.product_name,
      { offers }
    );
    setSaving(false);

    if (response.success) {
      toast.success('Offers updated');
      setManagingProduct(null);
      loadProducts();
    } else {
      toast.error(response.error || 'Failed to update offers');
    }
  };

  return (
    <div className="section seasons-section teams-section">
      <div className="seasons-columns settings-columns-narrow">
        <div className="settings-action-row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 className="section-title seasons-section-title" style={{ margin: 0 , padding: 4, display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Package size={24} />
            Affiliate Offer
          </h2>
        </div>

        <Card
          title="Affiliate Offer List"
          description="Manage the offer codes redeemable against each product."
          headerAction={
            <div style={{ minWidth: 'min(100%, 220px)' }}>
              <Input label="" value={search} onChange={setSearch} placeholder="Search product ID or name" />
            </div>
          }
        >
          {error && (
            <div className="alert alert-danger" style={{ marginBottom: '16px' }}>
              {error}
            </div>
          )}

          <div className="settings-table-wrap">
            <table className="settings-table affiliate-table">
              <thead>
                <tr>
                  <th>Product ID</th>
                  <th>Product Name</th>
                  <th>Offers</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredProducts.map((product) => (
                  <tr key={product.product_id}>
                    <td>{product.product_id}</td>
                    <td>{product.product_name || '-'}</td>
                    <td>{Object.keys(product.offers || {}).length}</td>
                    <td>
                      <div className="seasons-list-actions teams-list-actions">
                        <button
                          type="button"
                          className="seasons-view-button"
                          onClick={() => openManageOffers(product)}
                        >
                          Manage Offers
                          <Tag size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}

                {!loading && filteredProducts.length === 0 && (
                  <tr>
                    <td colSpan={7}>
                      <div className="seasons-footer-message">No products found.</div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {managingProduct && (
        <div className="seasons-modal-overlay" role="presentation" onClick={closeManageOffers}>
          <div
            className="seasons-modal-panel settings-modal-panel"
            style={{ width: 'min(760px, calc(100vw - 24px))' }}
            role="dialog"
            aria-modal="true"
            aria-label="Manage offers"
            onClick={(e) => e.stopPropagation()}
          >
            <Card
              title={`Manage Offers — ${managingProduct.product_name || managingProduct.product_id}`}
              description="Each offer maps an Offer ID to one or more redeemable codes (comma-separated)."
              headerAction={
                <button
                  type="button"
                  className="seasons-modal-close"
                  onClick={closeManageOffers}
                  aria-label="Close"
                >
                  <X size={16} />
                </button>
              }
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {offerRows.length > 0 && (
                  <div style={{ display: 'flex', gap: '10px', padding: '0 14px' }}>
                    <span style={{ flex: 1, ...columnLabelStyle }}>Offer ID</span>
                    <span style={{ flex: 1, ...columnLabelStyle }}>Codes (comma-separated)</span>
                    <span style={{ width: '68px', flex: 'none' }} />
                  </div>
                )}

                {offerRows.map((row, index) => (
                  <div
                    key={index}
                    className="card"
                    style={{ display: 'flex', gap: '10px', alignItems: 'center', padding: '10px 14px', boxShadow: 'none', border: '1px solid rgba(var(--color-border-rgb), 0.16)' }}
                  >
                    <div style={{ flex: 1 }}>
                      <input
                        className="form-input"
                        value={row.offerId}
                        onChange={(e) => updateOfferRow(index, 'offerId', e.target.value)}
                        placeholder="e.g., offer123"
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <input
                        className="form-input"
                        value={row.codes}
                        onChange={(e) => updateOfferRow(index, 'codes', e.target.value)}
                        placeholder="e.g., OFFER123, OFFER456"
                      />
                    </div>
                    <button
                      type="button"
                      className="list-delete-button"
                      onClick={() => removeOfferRow(index)}
                      aria-label="Remove offer"
                    >
                      Delete
                    </button>
                  </div>
                ))}

                {offerRows.length === 0 && (
                  <div style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', padding: '32px 16px',
                    border: '1px dashed rgba(var(--color-border-rgb), 0.3)', borderRadius: '12px', color: Colors.textLight,
                  }}
                  >
                    <Tag size={22} style={{ opacity: 0.4 }} />
                    <span style={{ fontSize: '13px' }}>No offers yet — add one to get started.</span>
                  </div>
                )}

                <Button variant="outline" className="btn-small" onClick={addOfferRow} style={{ alignSelf: 'flex-start' }}>
                  <Plus size={14} style={{ marginRight: '4px' }} />
                  Add Offer
                </Button>
              </div>

              <div
                className="button-group"
                style={{ marginTop: '18px', paddingTop: '16px', borderTop: '1px solid rgba(var(--color-border-rgb), 0.14)', justifyContent: 'flex-end' }}
              >
                <Button variant="outline" className="btn-small" onClick={closeManageOffers} disabled={saving}>
                  Cancel
                </Button>
                <Button variant="primary" className="btn-small" loading={saving} onClick={handleSaveOffers}>
                  Save
                </Button>
              </div>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}
